import React, { useState, useEffect } from "react";
import { Modal, Typography, TextField, TextDescription } from "@ann/web-components";
import { ModalContent, RoleNameWrapper, ErrorMessage } from "./EditRoleModal.styled";
import { updateRoleByUuid } from "../../../../services";
import { useToast } from "../../../../../../../components/toast";

const EditRoleModal = ({ 
  isOpen, 
  onClose, 
  roleUuid,
  roleName: initialRoleName = "", 
  roleDescription: initialRoleDescription = "",
  onSave 
}) => {
  const [roleName, setRoleName] = useState(initialRoleName);
  const [roleDescription, setRoleDescription] = useState(initialRoleDescription);
  const [isSaving, setIsSaving] = useState(false);
  const [touched, setTouched] = useState(false);
  const toast = useToast();

  // Reset form when modal opens with new data
  useEffect(() => {
    if (isOpen) {
      setRoleName(initialRoleName);
      setRoleDescription(initialRoleDescription);
      setTouched(false);
    }
  }, [isOpen, initialRoleName, initialRoleDescription]);

  const isFieldEmpty = !roleName.trim();
  const showError = touched && isFieldEmpty;
  
  // Check if there are changes
  const hasChanges = roleName !== initialRoleName || roleDescription !== initialRoleDescription;

  const handleSave = async () => {
    if (isFieldEmpty) return;
    
    try {
      setIsSaving(true);
      
      const roleData = {
        uuid: roleUuid,
        name: roleName.trim(),
        description: roleDescription.trim(),
        metadata: {},
        updated_by_id: 0,
      };

      await updateRoleByUuid(roleUuid, roleData);
      
      toast.success({ 
        message: "Role updated successfully", 
        canClose: true 
      });
      
      if (onSave) {
        onSave({
          success: true,
          data: roleData,
        });
      }
      
      handleClose();
    } catch (error) {
      console.error("Error saving role:", error);
      
      toast.error({ 
        message: error.message || "Error updating role", 
        canClose: true 
      });
      
      if (onSave) {
        onSave({
          success: false,
          error: error,
        });
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    setRoleName(initialRoleName);
    setRoleDescription(initialRoleDescription);
    setIsSaving(false);
    setTouched(false);
    onClose();
  };

  const handleRoleNameChange = (value) => {
    setRoleName(value);
    if (!touched && value.length > 0) {
      setTouched(true);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} width="420px">
      <Modal.Header>
        <Modal.Header.Title>Role details</Modal.Header.Title>
      </Modal.Header>
      <Modal.Body>
        <ModalContent>
          <RoleNameWrapper>
            <TextField 
              label="Role name" 
              onChange={handleRoleNameChange} 
              size="small" 
              value={roleName}
              required
            />
            {showError ? (
              <ErrorMessage>Role name is required</ErrorMessage>
            ) : (
              <Typography category="body" size="small" weight="regular" color="body">
                Choose a name that clearly defines this role.
              </Typography>
            )}
          </RoleNameWrapper>
      
          <TextDescription
            height="148px"
            label="Description (optional)"
            maxCharacters={200}
            onChange={(value) => setRoleDescription(value)}
            placeholder="Enter text here..."
            size="small"
            value={roleDescription}
          />
        </ModalContent>
      </Modal.Body>
      <Modal.Actions align="end">
        <Modal.Button
          label="Cancel"
          onClick={handleClose}
          type="secondary"
        />
        <Modal.Button
          label={isSaving ? "Saving..." : "Save Changes"}
          onClick={handleSave}
          disabled={isFieldEmpty || isSaving || !hasChanges}
        />
      </Modal.Actions>
    </Modal>
  );
};

export default EditRoleModal;
